import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { AppStateFacade } from '@app-state';
import { IAlert } from '@models';
import {
    AlertsService,
    SignalRService,
} from '@services';

import {
    BehaviorSubject,
    merge,
    Observable,
    ReplaySubject,
    Subject,
} from 'rxjs';
import {
    filter,
    finalize,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';

@Component({
    selector: 'ui-alerts',
    templateUrl: './ui-alerts.component.html',
    styleUrls: ['./ui-alerts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiAlertsComponent implements OnInit, OnDestroy {
    @Input()
    public seeAllPlaceholder = 'See all';
    @Input()
    public title = 'Alerts';
    @Input()
    public noAlertsPlaceholder = 'No alerts';
    @Input()
    public settingsTooltip = 'Open Notification Preferences';
    @Output()
    public openSettings = new EventEmitter();

    @ViewChild(MatMenuTrigger, { static: true })
    public alertMenuTrigger!: MatMenuTrigger;

    @ViewChild('menuSubhead', { static: false })
    public menuSubhead?: ElementRef<HTMLDivElement>;

    public hasNotifications$!: Observable<boolean>;
    public alerts$: Observable<IAlert[]>;
    public loading$ = new BehaviorSubject(false);
    public shouldLoad = true;

    private _top = 5;
    private _severityMap: any = {
        0: 'Info',
        1: 'Success',
        2: 'Warn',
        3: 'Error',
        4: 'Fatal',
    };
    private _destroyed$ = new Subject<void>();
    private _trigger$ = new ReplaySubject<void>(1);

    constructor(
        private _appState: AppStateFacade,
        private _alertsService: AlertsService,
        private _signalRService: SignalRService,
        private _zone: NgZone,
    ) {
        this.alerts$ = this._trigger$.pipe(
            switchMap(() => this._alertsService.getUnreadList(this._top)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )),
            tap(() => {
                this.shouldLoad = false;
            }),
            map(res => res.data),
        );
    }

    ngOnInit() {
        const isGranted$ = this._appState.user.permissions.isGranted('Alerts.View');

        this.hasNotifications$ =
            isGranted$.pipe(
                tap(isGranted => {
                    if (!isGranted && this.alertMenuTrigger.menuOpen) {
                        this.alertMenuTrigger.closeMenu();
                    }
                }),
                filter(isGranted => isGranted),
                switchMap(
                    () => merge(this._alertsService.getUnreadCount(), this._alertsService.count$),
                ),
                map(count => count > 0),
            );

        isGranted$.pipe(
            filter(isGranted => isGranted),
            switchMap(() => this._signalRService.notification$),
            takeUntil(this._destroyed$),
        ).subscribe(this._onNotificationReceived);

        this.alertMenuTrigger.menuOpened
            .pipe(
                switchMap(() => this._zone.onStable.pipe(take(1))),
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._loadAlerts();
                this.menuSubhead!.nativeElement.focus();
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    private _loadAlerts() {
        if (this.shouldLoad) {
            this.loading$.next(true);
            this._trigger$.next();
        }
    }

    private _onNotificationReceived = (alert: IAlert) => {
        if (alert.severity >= 3) {
            alert.severity = this._severityMap[alert.severity];
            // If a signalR notification arrived
            // the alerts should be loaded on next expand.
            this.shouldLoad = true;
            if (this.alertMenuTrigger && this.alertMenuTrigger.menuOpen) {
                this._loadAlerts();
            }
        }
    }
}
